/* eslint-disable react-hooks/exhaustive-deps*/
import React, { useState, useEffect, useRef } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import css from './DashboardAddListingWrapper.module.css';
import classnames from 'classnames';

import { Tabs, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';

import { withRouter } from 'react-router-dom';

import DashboardAddButton from '../../elements/dashboard-add-button/DashboardAddButton';
import Overview from './overview/Overview';
import Services from './services/Services';
import Gallery from './gallery/Gallery';
import ProgramRequiredModal from '../../elements/program-required-modal/ProgramRequiredModal';
import { getAllPrograms } from '../../../custom-ducks/programs.duck';

import { faTimes } from '@fortawesome/free-solid-svg-icons';

function DashboardAddListingWrapper(props) {
  const {
    onCreateListingDraft,
    onUpdateListing,
    onPublishListingDraft,
    onImageUpload,
    listing,
    params,
    images,
    currentUser,
    location,
    programs,
    programsFetching,
    onGetAllPrograms,
  } = props;
  const focusField = (location && location.state && location.state.focusField) || null;

  // "You have zero programs" nudge — an ad can't be activated without a
  // linked program (REQUIRED_FIELD_CHECKS in DashboardListingWrapper.js),
  // so warn on landing in the NEW-ad wizard rather than letting the coach
  // discover it deep in Overview/Services. Not shown when editing an
  // existing ad (params.type !== 'new') — by then they've already been
  // through this once. Checked exactly once per mount.
  const [showProgramRequired, setShowProgramRequired] = useState(false);
  const [programCheckDone, setProgramCheckDone] = useState(false);
  // True -> false transition tracking, same reasoning as elsewhere: on
  // first render programsFetching may already read `false` before
  // onGetAllPrograms() below has had a chance to flip it.
  const programFetchStartedRef = useRef(false);

  useEffect(() => {
    if (params.type === 'new') {
      onGetAllPrograms();
    }
  }, []);

  useEffect(() => {
    if (params.type !== 'new') return;
    if (programsFetching) {
      programFetchStartedRef.current = true;
      return;
    }
    if (programCheckDone || !programFetchStartedRef.current) return;
    const hasPrograms = Array.isArray(programs) && programs.length > 0;
    setProgramCheckDone(true);
    setShowProgramRequired(!hasPrograms);
  }, [programsFetching]);

  const closeProgramRequiredModal = () => setShowProgramRequired(false);
  const handleProgramRequiredCreateNow = () => {
    setShowProgramRequired(false);
    // Land on the Programs page with a flag so it opens the create-choice
    // modal immediately instead of the coach having to click again.
    props.history.push({ pathname: '/v-coach/programs', state: { openCreate: true } });
  };
  const [tabState, setTabState] = useState({
    service: true,
    gallery: true,
    preview: true,
  });
  const [tabIndex, setTabIndex] = useState(1);

  const updateTabs = () => {
    switch (params.tab) {
      case 'overview':
        setTabIndex(0);
        break;

      case 'service':
        setTabState({ service: false, gallery: true, preview: true });
        setTabIndex(1);
        break;

      case 'gallery':
        setTabState({ service: false, gallery: false, preview: true });
        setTabIndex(2);
        break;

      default:
        setTabIndex(0);
        break;
    }
  };

  useEffect(() => updateTabs(), [params.tab]);
  useEffect(() => {
    // Deep links from the "Activate" required-field check request a
    // specific tab (via updateTabs() above) — don't stomp on that.
    if (params.slug === 'edit' && !focusField) {
      setTabIndex(0);
    }
  }, []);

  // Stripe/subscription/bio completeness used to be re-checked here on
  // every render and would bounce the coach straight back to /listings
  // the instant they landed on this page — before they'd typed a single
  // field. That's exactly the "warning at the beginning" behavior we
  // don't want. Per Subscription Gating Spec, those checks now happen
  // exactly once, at Submit time, in Gallery.js's onSubmit /
  // getPublishReadiness + getMissingProfileItems (via
  // CompleteProfileModal) — the coach can draft freely up to that point,
  // and the draft is saved regardless of whether the gate passes.

  // Custom stepper (circles + connecting line) replacing react-tabs'
  // TabList. `Tabs` below is used purely as a controlled panel-switcher
  // (selectedIndex={tabIndex}, no TabList child) — react-tabs derives
  // panel visibility straight from selectedIndex/TabPanel position, so
  // it doesn't need a TabList to function. This gave full control over
  // the step-circle layout instead of fighting react-tabs' Tab markup.
  const STEPS = [
    { label: 'Overview', disabled: false },
    { label: 'Services', disabled: tabState.service },
    { label: 'Gallery', disabled: tabState.gallery },
  ];

  return (
    <div className={css.wrapper}>
      {/*<div className={css.add}>*/}
      {/*  <DashboardAddButton onClick={() => props.history.push('/listings')} icon={faTimes} />*/}
      {/*</div>*/}
      <div className={css.stepperCard}>
        <div className={css.stepperRow}>
          {STEPS.map((step, i) => (
            <React.Fragment key={step.label}>
              <div
                className={classnames(css.step, step.disabled && css.stepDisabled)}
                onClick={() => !step.disabled && setTabIndex(i)}
                role="button"
                tabIndex={step.disabled ? -1 : 0}
                aria-disabled={step.disabled}
                aria-current={tabIndex === i ? 'step' : undefined}
              >
                <span
                  className={classnames(
                    css.stepCircle,
                    (tabIndex === i || tabIndex > i) && css.stepCircleActive
                  )}
                >
                  {i + 1}
                </span>
                <span
                  className={classnames(css.stepLabel, tabIndex === i && css.stepLabelActive)}
                >
                  {step.label}
                </span>
              </div>
              {i < STEPS.length - 1 && <span className={css.stepConnector} />}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className={css.contentCard}>
        <Tabs selectedIndex={tabIndex} onSelect={i => setTabIndex(i)}>
          <TabPanel>
            <Overview
              listing={listing}
              onUpdateListing={onUpdateListing}
              onCreateListingDraft={onCreateListingDraft}
              currentUser={currentUser}
              setTabIndex={setTabIndex}
              focusField={params.tab === 'overview' ? focusField : null}
            />
          </TabPanel>

          <TabPanel>
            <Services
              listing={listing}
              onUpdateListing={onUpdateListing}
              setTabIndex={setTabIndex}
              // Passed so Services can send the coach's own profile
              // (bio, tagline, specialties, ...) as context to the AI
              // listing-content generator. See generateListingContent.js.
              currentUser={currentUser}
              focusField={params.tab === 'service' ? focusField : null}
            />
          </TabPanel>

          <TabPanel>
            <Gallery
              listing={listing}
              onUpdateListing={onUpdateListing}
              onImageUpload={onImageUpload}
              images={images}
              onPublishListingDraft={onPublishListingDraft}
              setTabIndex={setTabIndex}
              // Pass currentUser so Gallery can run the publish-time
              // gate (tier 0 + profile completeness) before calling
              // onPublishListingDraft. See SUBSCRIPTION-GATING-SPEC §1, §5.2.
              currentUser={currentUser}
              focusField={params.tab === 'gallery' ? focusField : null}
            />
          </TabPanel>

          {/* <TabPanel>
            <div className={css.listings}>preview</div>
          </TabPanel> */}
        </Tabs>
      </div>

      <ProgramRequiredModal
        isOpen={showProgramRequired}
        onClose={closeProgramRequiredModal}
        onCreateNow={handleProgramRequiredCreateNow}
      />
    </div>
  );
}

const mapStateToProps = state => {
  const { programs, programs_fetching } = state.programs;
  return {
    programs,
    programsFetching: programs_fetching,
  };
};

const mapDispatchToProps = dispatch => ({
  onGetAllPrograms: () => dispatch(getAllPrograms()),
});

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(DashboardAddListingWrapper);
